'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Settings, Save, Plus, Edit3, Trash2, User, Shield, Bell,
  Building2, CreditCard, CheckCircle, X, Eye, EyeOff
} from 'lucide-react';

type SettingsTab = 'loan-rules' | 'company' | 'users' | 'notifications';

const users = [
  { id: 1, name: 'Siosaia Taufa', email: 'siosaia@vatulend.vu', role: 'Admin', status: 'Active', lastLogin: '2026-08-05' },
  { id: 2, name: 'Mere Vanuatu', email: 'mere@vatulend.vu', role: 'Loan Officer', status: 'Active', lastLogin: '2026-08-04' },
  { id: 3, name: 'Rex Naupa', email: 'rex@vatulend.vu', role: 'Collector', status: 'Active', lastLogin: '2026-08-03' },
];

const roleColors: Record<string, string> = {
  Admin: 'bg-primary/10 text-primary',
  'Loan Officer': 'bg-info/10 text-info',
  Collector: 'bg-warning/10 text-warning',
};

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('loan-rules');
  const [saved, setSaved] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [loanRules, setLoanRules] = useState({ defaultRate: '20', maxRate: '30', minTerm: '7', maxTerm: '90', lateFeeRate: '10', loanPrefix: 'VL', currency: 'VT' });
  const [company, setCompany] = useState({ name: 'VatuLend', address: 'Port Vila, Efate, Vanuatu', phone: '+678 555 0000', email: 'info@vatulend.vu', abn: 'VU-BIZ-001234', receiptFooter: 'Thank you for your payment. VatuLend — Private Lending Management.' });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'Loan Officer', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [notifTemplates, setNotifTemplates] = useState({
    dueSoon: 'Dear {name}, your loan {loanId} of {amount} is due on {date}. Please arrange payment. — VatuLend',
    overdue: 'Dear {name}, your loan {loanId} is overdue by {days} days. Outstanding: {amount}. Contact us immediately. — VatuLend',
    received: 'Dear {name}, we received your payment of {amount} for loan {loanId}. Receipt: {receipt}. — VatuLend',
    newContrib: 'Dear {name}, your capital contribution of {amount} has been received. Thank you. — VatuLend',
  });

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
    { id: 'loan-rules', label: 'Loan Rules', icon: <CreditCard size={15} /> },
    { id: 'company', label: 'Company Profile', icon: <Building2 size={15} /> },
    { id: 'users', label: 'User Management', icon: <User size={15} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={15} /> },
  ];

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Configure business rules, company profile, users, and notifications</p>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${saved ? 'bg-positive text-primary-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}>
              {saved ? <><CheckCircle size={15} /> Saved!</> : <><Save size={15} /> Save Changes</>}
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Tabs */}
          <div className="lg:w-52 shrink-0">
            <div className="bg-card border border-border rounded-xl p-2 space-y-0.5">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeTab === 'loan-rules' && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-5">
                <div className="flex items-center gap-2 mb-2">
                  <Settings size={16} className="text-primary" />
                  <h2 className="font-bold text-foreground">Loan Product Configuration</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Default Interest Rate (%)', key: 'defaultRate', type: 'number' },
                    { label: 'Maximum Interest Rate (%)', key: 'maxRate', type: 'number' },
                    { label: 'Minimum Term (days)', key: 'minTerm', type: 'number' },
                    { label: 'Maximum Term (days)', key: 'maxTerm', type: 'number' },
                    { label: 'Late Fee Rate (%)', key: 'lateFeeRate', type: 'number' },
                    { label: 'Loan Number Prefix', key: 'loanPrefix', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{f.label}</label>
                      <input
                        type={f.type}
                        value={(loanRules as any)[f.key]}
                        onChange={e => setLoanRules(r => ({ ...r, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Currency</label>
                  <select value={loanRules.currency} onChange={e => setLoanRules(r => ({ ...r, currency: e.target.value }))} className="w-full sm:w-48 px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="VT">VT — Vanuatu Vatu</option>
                    <option value="USD">USD — US Dollar</option>
                    <option value="AUD">AUD — Australian Dollar</option>
                  </select>
                </div>
                <div className="pt-2">
                  <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                    <Save size={14} /> Update Loan Rules
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'company' && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-5">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 size={16} className="text-primary" />
                  <h2 className="font-bold text-foreground">Company Profile</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Company Name', key: 'name' },
                    { label: 'Business Registration', key: 'abn' },
                    { label: 'Phone', key: 'phone' },
                    { label: 'Email', key: 'email' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{f.label}</label>
                      <input value={(company as any)[f.key]} onChange={e => setCompany(c => ({ ...c, [f.key]: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Address</label>
                  <input value={company.address} onChange={e => setCompany(c => ({ ...c, address: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Receipt Footer Message</label>
                  <textarea value={company.receiptFooter} onChange={e => setCompany(c => ({ ...c, receiptFooter: e.target.value }))} rows={3} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none" />
                </div>
                <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                  <Save size={14} /> Edit Company Profile
                </button>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <div className="flex items-center gap-2">
                      <Shield size={15} className="text-primary" />
                      <h2 className="font-bold text-foreground text-sm">User Management</h2>
                    </div>
                    <button onClick={() => setShowAddUser(true)} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors">
                      <Plus size={13} /> Add New User
                    </button>
                  </div>
                  <div className="divide-y divide-border">
                    {users.map(u => (
                      <div key={u.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center">
                            <span className="text-xs font-bold text-primary">{u.name.split(' ').map(n => n[0]).join('')}</span>
                          </div>
                          <div>
                            <p className="font-semibold text-foreground text-sm">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email} · Last login: {u.lastLogin}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${roleColors[u.role] || 'bg-muted text-muted-foreground'}`}>{u.role}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-positive/10 text-positive font-semibold">{u.status}</span>
                          <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Edit3 size={13} /></button>
                          <button className="p-1.5 rounded-lg hover:bg-negative/10 text-muted-foreground hover:text-negative transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add User Form */}
                {showAddUser && (
                  <div className="bg-card border border-primary/20 rounded-xl p-5 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-foreground text-sm">+ Add New User</h3>
                      <button onClick={() => setShowAddUser(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={14} /></button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Full Name</label>
                        <input value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} placeholder="Full name" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Email</label>
                        <input value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} placeholder="user@vatulend.vu" type="email" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Role</label>
                        <select value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                          <option>Admin</option>
                          <option>Loan Officer</option>
                          <option>Collector</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Password</label>
                        <div className="relative">
                          <input value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} type={showPwd ? 'text' : 'password'} placeholder="••••••••" className="w-full px-3 py-2 pr-9 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                          <button onClick={() => setShowPwd(s => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setShowAddUser(false)} className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                      <button onClick={() => { setShowAddUser(false); handleSave(); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                        <CheckCircle size={14} /> Create User
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="bg-card border border-border rounded-xl p-6 space-y-5">
                <div className="flex items-center gap-2 mb-2">
                  <Bell size={16} className="text-primary" />
                  <h2 className="font-bold text-foreground">Notification Templates</h2>
                </div>
                <p className="text-xs text-muted-foreground">Use variables: {'{name}'}, {'{loanId}'}, {'{amount}'}, {'{date}'}, {'{days}'}, {'{receipt}'}</p>
                {[
                  { key: 'dueSoon', label: 'Loan Due Soon' },
                  { key: 'overdue', label: 'Overdue Reminder' },
                  { key: 'received', label: 'Payment Received' },
                  { key: 'newContrib', label: 'New Capital Contribution' },
                ].map(t => (
                  <div key={t.key}>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">{t.label}</label>
                    <textarea
                      value={(notifTemplates as any)[t.key]}
                      onChange={e => setNotifTemplates(n => ({ ...n, [t.key]: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                    />
                  </div>
                ))}
                <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                  <Save size={14} /> Save Notification Template
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
